import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiRefreshCw,
  FiShield,
} from "react-icons/fi";
import PayoutBulkUploadFlowSteps from "./PayoutBulkUploadFlowSteps";
import { payoutApi } from "./api/payoutApiClient";
import {
  compactBody,
  errorMessage,
  isPayoutMockMode,
  pickString,
  readSessionJson,
  stableIdempotencyKey,
  verifyStepUp,
  writeSessionJson,
} from "./api/payoutMutationHelpers";
import { validateOtpCode } from "./api/payoutValidation";
import { showPayoutNotification } from "./PayoutNotification";

const defaultBulkBeneficiaryDraft = {
  fileName: "-",
  rows: [] as Record<string, unknown>[],
};

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-6 border-b border-[#E0E5F2] py-3 last:border-b-0">
      <span className="text-sm font-medium text-[#505E8E]">{label}</span>
      <div className="max-w-[220px] text-right text-sm font-semibold text-[#1B163B]">
        {value}
      </div>
    </div>
  );
}

function PayoutBeneficiaryBulkUploadVerify() {
  const navigate = useNavigate();
  const draft = readSessionJson(
    "payoutBulkBeneficiaryDraft",
    defaultBulkBeneficiaryDraft,
  );
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const method = "authenticator" as const;
  const [submitting, setSubmitting] = useState(false);
  const [, setSubmitError] = useState("");
  const showSubmitError = React.useCallback((message: string) => {
    setSubmitError(message);
    showPayoutNotification({ tone: "error", message });
  }, []);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const code = useMemo(() => otp.join(""), [otp]);
  const ready = !validateOtpCode(code);
  const instruction = "Enter the 6-digit code from your Google Authenticator app.";

  const updateDigit = (index: number, value: string) => {
    const nextValue = value.replace(/\D/g, "").slice(-1);
    const nextOtp = [...otp];
    nextOtp[index] = nextValue;
    setOtp(nextOtp);
    if (nextValue && index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 6).split("");
    if (!digits.length) return;
    const nextOtp = ["", "", "", "", "", ""];
    digits.forEach((digit, index) => {
      nextOtp[index] = digit;
    });
    setOtp(nextOtp);
    inputsRef.current[Math.min(digits.length, 5)]?.focus();
  };

  const handleVerifyAndSubmit = async () => {
    setSubmitError("");
    const otpError = validateOtpCode(
      code,
      "Bulk upload beneficiaries: enter the 6-digit Google Authenticator code to submit this batch.",
    );
    if (otpError) {
      showSubmitError(otpError);
      return;
    }
    if (!draft.rows.length) {
      showSubmitError("Bulk upload beneficiaries: upload and review a beneficiary file before submitting.");
      return;
    }
    if (submitting) return;

    if (isPayoutMockMode()) {
      navigate("/payouts/beneficiaries/bulk-upload/confirm");
      return;
    }

    setSubmitting(true);
    try {
      const items = draft.rows.map((row: Record<string, unknown>) =>
        compactBody({
          funding_account_id: pickString(row, [
            "funding_account_id",
            "fundingAccountId",
            "payout_account_id",
            "account_id",
          ]),
          name: pickString(row, [
            "name",
            "name_entered",
            "beneficiary_name",
          ]),
          category: pickString(row, ["category"], "vendor"),
          mobile: pickString(row, ["mobile", "mobile_number"]),
          email: pickString(row, ["email"]),
          account_number: pickString(row, ["account_number", "accountNumber"]),
          ifsc: pickString(row, ["ifsc", "ifsc_code", "ifscCode"]),
          account_type: pickString(row, ["account_type", "accountType"], "savings"),
          bank_name: pickString(row, ["bank_name", "bankName"]),
          branch_name: pickString(row, ["branch_name", "branchName", "branch"]),
          remarks: pickString(row, ["remarks", "purpose"]),
        }),
      );
      const body = { items };
      const validation = await payoutApi.validateBulkBeneficiaries(
        body,
        stableIdempotencyKey("validate-bulk-beneficiaries", draft.fileName),
      );
      const invalidRow = Array.isArray(validation.results)
        ? validation.results.find((result) => {
            const rowResult = result as Record<string, unknown>;
            return rowResult.ok === false;
          }) as Record<string, unknown> | undefined
        : undefined;
      if (invalidRow) {
        const rowNumber =
          typeof invalidRow.index === "number" ? invalidRow.index + 1 : "-";
        const errors = Array.isArray(invalidRow.errors)
          ? invalidRow.errors.join(", ")
          : "Validation failed";
        throw new Error(`Row ${rowNumber}: ${errors}`);
      }
      const stepUpToken = await verifyStepUp({
        purpose: "CREATE_BULK_BENEFICIARIES",
        method,
        code,
        subjectId: draft.fileName,
      });
      const result = await payoutApi.createBulkBeneficiaries(
        body,
        stableIdempotencyKey("create-bulk-beneficiaries", draft.fileName),
        stepUpToken,
      );
      writeSessionJson("payoutBulkBeneficiaryResult", result);
      navigate("/payouts/beneficiaries/bulk-upload/confirm");
    } catch (error) {
      showSubmitError(errorMessage(error, "Bulk upload beneficiaries: unable to submit this batch. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
          Verify Beneficiary Upload
        </h1>
        <p className="mt-1 text-sm font-medium text-[#505E8E]">
          Verify this beneficiary batch before it is submitted.
        </p>
      </div>

      <PayoutBulkUploadFlowSteps activeStep={3} />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,760px)_380px]">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="mb-6 flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] bg-[#1B163B0D] text-xl text-[#1B163B]">
              <FiShield />
            </div>
            <div>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                Verify Beneficiary Batch
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                {instruction}
              </p>
            </div>
          </div>

          <div>
            <div className="rounded-[8px] border border-[#1B163B] bg-[#1B163B0D] px-5 py-4 text-left">
              <div className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-[6px] bg-[#DCFCE7] text-[#16A34A]">
                    <FiShield />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-[#1B163B]">
                      Google Authenticator
                    </span>
                    <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                      Recommended for beneficiary operations
                    </span>
                  </span>
                </span>
                <FiCheck className="text-lg text-[#16A34A]" />
              </div>
            </div>
          </div>

          <div className="mt-8">
            <label className="mb-3 block text-sm font-semibold text-[#1B163B]">
              Verification Code
            </label>
            <div className="flex flex-wrap gap-3">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(element) => {
                    inputsRef.current[index] = element;
                  }}
                  value={digit}
                  onChange={(event) => updateDigit(index, event.target.value)}
                  onPaste={(event) => {
                    event.preventDefault();
                    handlePaste(event.clipboardData.getData("text"));
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Backspace" && !otp[index] && index > 0) {
                      inputsRef.current[index - 1]?.focus();
                    }
                  }}
                  inputMode="numeric"
                  className="h-12 w-12 rounded-[6px] border border-[#E0E5F2] bg-white text-center text-lg font-semibold text-[#1B163B] outline-none focus:border-[#1B163B]"
                />
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-5 text-sm font-semibold">
              <button
                type="button"
                onClick={() => setOtp(["", "", "", "", "", ""])}
                className="flex items-center gap-2 text-[#1B163B]"
              >
                <FiRefreshCw />
                Resend code
              </button>
            </div>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-4">
            <Link
              to="/payouts/beneficiaries/bulk-upload/review"
              className="flex h-10 min-w-[140px] items-center justify-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
            >
              <FiArrowLeft />
              Back
            </Link>
            <button
              type="button"
              onClick={handleVerifyAndSubmit}
              disabled={!ready || submitting}
              style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
              className={
                "flex h-10 min-w-[180px] items-center justify-center gap-3 rounded-[6px] px-7 text-sm font-semibold text-white " +
                (ready && !submitting ? "hover:brightness-95" : "cursor-not-allowed opacity-50")
              }
            >
              {submitting ? "Submitting..." : "Verify & Continue"}
              <FiArrowRight />
            </button>
            {!ready && (
              <p className="w-full text-xs font-semibold text-[#EF4444]">
                Enter all 6 digits to enable Continue
              </p>
            )}
          </div>
        </section>

        <aside className="h-fit rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="text-[18px] font-semibold text-[#1B163B]">
            Batch Summary
          </h2>
          <div className="my-5 h-px bg-[#E0E5F2]" />
          <SummaryRow label="Beneficiaries" value={draft.rows.length} />
          <SummaryRow label="Verified Rows" value={draft.rows.length} />
          <SummaryRow label="Verification" value="Google Authenticator" />
          <SummaryRow label="Status" value="Ready to submit" />
          <div className="mt-5 rounded-[6px] bg-[#DCFCE7] px-4 py-3 text-sm font-medium text-[#166534]">
            Beneficiary batch is locked while verification is in progress.
          </div>
        </aside>
      </div>
    </div>
  );
}

export default PayoutBeneficiaryBulkUploadVerify;
